import React, { createContext, useContext, useState, useEffect } from 'react';
import { LysepAPI } from './ApiContext';
import { PERMISSIONS, ROLES } from '../constants/permissions';
import { useSelector } from 'react-redux';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [isRoot, setIsRoot] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redux store'dan session bilgisini al
  const session = useSelector(state => state.session);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Önce mevcut yetkileri temizle
      setPermissions([]);
      setRole(null);
      setIsRoot(false);
      setIsAdmin(false);

      const response = await LysepAPI.get('/api/user/permissions');

      if (response.data && response.data.state) {
        const data = response.data.data;
        console.log('Permission API response:', data);
        setPermissions(data.permissions || []);
        setRole(data.role);
        setIsRoot(data.isRoot || false);
        setIsAdmin(data.isAdmin || false);
        console.log('Set permissions:', data.permissions);
        console.log('Set role:', data.role);
        console.log('Set isRoot:', data.isRoot);
        console.log('Set isAdmin:', data.isAdmin);
      } else {
        console.log('Invalid response format:', response.data);
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Permission fetch error:', error);
      console.log('Setting fallback permissions for guest user');
      setError(error.message);
      // Set default permissions for guest user if API fails
      setPermissions([]);
      setRole(ROLES.GUEST);
      setIsRoot(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  // Belirli yetkiyi kontrol et
  const checkPermission = async (permission) => {
    try {
      const response = await LysepAPI.get(`/api/user/permissions/check/${permission}`);
      if (response.data && response.data.state) {
        return response.data.data.hasPermission;
      }
      return false;
    } catch (error) {
      console.error('Permission check error:', error);
      return false;
    }
  };

  useEffect(() => {
    // Sadece kullanıcı giriş yapmışsa yetkileri çek
    if (session.Authenticated && session.User) {
      console.log('User authenticated, fetching permissions for:', session.User.username);
      fetchPermissions();
    } else {
      console.log('User not authenticated, clearing permissions');
      // Kullanıcı çıkış yapmışsa yetkileri temizle
      setPermissions([]);
      setRole(null);
      setIsRoot(false);
      setIsAdmin(false);
      setLoading(false);
      setError(null);
    }
  }, [session.Authenticated, session.User]);

  const hasPermission = (permission) => {
    if (isRoot) return true;
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (isRoot) return true;
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasRole = (roleName) => {
    return role === roleName;
  };

  const hasAnyRole = (roleList) => {
    return roleList.includes(role);
  };

  const canAccessModule = (moduleName) => {
    console.log('canAccessModule called with:', moduleName);
    console.log('Current permissions:', permissions);
    console.log('isRoot:', isRoot);
    console.log('loading:', loading);

    // Eğer henüz yükleniyorsa veya hata varsa, erişim verme
    if (loading) {
      console.log('Still loading permissions, denying access to:', moduleName);
      return false;
    }

    if (isRoot) {
      console.log('User is root, allowing access to:', moduleName);
      return true;
    }

    let result = false;
    switch (moduleName) {
      case 'dashboard':
        result = hasPermission(PERMISSIONS.SERVER_VIEW_UTILIZATION);
        console.log('Dashboard access:', result);
        break;
      case 'servers':
      case 'credentials':
        // These modules are strictly root-only
        result = false;
        console.log(`${moduleName} access (non-root):`, result);
        break;
      case 'users':
        result = hasAnyPermission([
          PERMISSIONS.USER_CREATE,
          PERMISSIONS.USER_READ,
          PERMISSIONS.USER_UPDATE,
          PERMISSIONS.USER_DELETE,
          PERMISSIONS.USER_LIST
        ]);
        console.log('Users access:', result);
        break;
      case 'piratecall':
        result = hasAnyPermission([
          PERMISSIONS.PIRATECALL_QUERY,
          PERMISSIONS.PIRATECALL_QUERY_GSM,
          PERMISSIONS.PIRATECALL_QUERY_ADDRESS,
          PERMISSIONS.PIRATECALL_QUERY_SGK,
          PERMISSIONS.PIRATECALL_QUERY_RELATIONSHIPS,
          PERMISSIONS.PIRATECALL_QUERY_HELPERS
        ]);
        console.log('PirateCall access:', result);
        break;
      case 'contacts':
        result = hasPermission(PERMISSIONS.CONTACT_MANAGEMENT);
        console.log('Contacts access:', result);
        break;
      case 'profile':
        // Profil modülü herkese açık
        result = true;
        console.log('Profile access:', result);
        break;
      case 'roles':
        result = hasPermission(PERMISSIONS.USER_ASSIGN_ROLE);
        console.log('Roles access:', result);
        break;
      case 'services':
        result = hasPermission(PERMISSIONS.SERVER_MANAGE_SYSTEM);
        console.log(`${moduleName} access:`, result);
        break;
      default:
        console.log('Unknown module:', moduleName);
        result = false;
    }

    console.log('Final result for', moduleName, ':', result);
    return result;
  };

  // Rol yönetimi fonksiyonları
  const getAllRoles = async () => {
    try {
      const response = await LysepAPI.get('/api/user/roles');
      if (response.data && response.data.state) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Get roles error:', error);
      return [];
    }
  };

  const getRoleById = async (id) => {
    try {
      const response = await LysepAPI.get(`/api/user/roles/${id}`);
      if (response.data && response.data.state) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Get role error:', error);
      return null;
    }
  };

  const createRole = async (roleData) => {
    try {
      const response = await LysepAPI.post('/api/user/roles', roleData);
      if (response.data && response.data.state) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Create role error:', error);
      throw error;
    }
  };

  const updateRole = async (id, roleData) => {
    try {
      const response = await LysepAPI.put(`/api/user/roles/${id}`, roleData);
      if (response.data && response.data.state) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Update role error:', error);
      throw error;
    }
  };

  const deleteRole = async (id) => {
    try {
      const response = await LysepAPI.delete(`/api/user/roles/${id}`);
      if (response.data && response.data.state) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Delete role error:', error);
      throw error;
    }
  };

  const assignRoleToUser = async (userId, role) => {
    try {
      const response = await LysepAPI.post(`/api/user/${userId}/role`, { role });
      if (response.data && response.data.state) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message || 'Failed to assign role' };
      }
    } catch (error) {
      console.error('Assign role error:', error);
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to assign role' };
    }
  };

  const removeRoleFromUser = async (userId) => {
    try {
      const response = await LysepAPI.delete(`/api/user/${userId}/role`);
      if (response.data && response.data.state) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Remove role error:', error);
      throw error;
    }
  };

  const getUsersByRole = async (roleName) => {
    try {
      const response = await LysepAPI.get(`/api/user/roles/${roleName}/users`);
      if (response.data && response.data.state) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Get users by role error:', error);
      return [];
    }
  };

  return (
    <PermissionContext.Provider value={{
      permissions,
      role,
      isRoot,
      isAdmin,
      loading,
      error,
      hasPermission,
      hasAnyPermission,
      hasRole,
      hasAnyRole,
      canAccessModule,
      checkPermission,
      refreshPermissions: fetchPermissions,
      // Rol yönetimi
      getAllRoles,
      getRoleById,
      createRole,
      updateRole,
      deleteRole,
      assignRoleToUser,
      removeRoleFromUser,
      getUsersByRole,
      PERMISSIONS,
      ROLES
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

export { PermissionContext };
export default PermissionProvider;
